/**
 * Phase 10D: Centralized strings for renewals (i18n-ready).
 * Replace with t('renewals.xxx') when react-i18next is configured.
 */

const en = {
  renewals: {
    dashboard: {
      title: 'Renewal Intelligence',
      stats: { expiring: 'Expiring Soon', savings: 'Potential Savings', recommendations: 'Better Deals', checks: 'Checks Run' },
      lastUpdated: 'Last updated',
      newDealsFound: '{{count}} new better deal(s) found!',
      upcoming: 'Upcoming renewals',
      viewCalendar: 'Calendar view',
      comparePolicies: 'Compare policies',
      noPolicies: 'No policies expiring soon.',
      runCheck: 'Check for better deals',
      checkNow: 'Check now',
    },
    card: {
      daysLeft: 'days left',
      expires: 'Expires',
      premium: 'Premium',
      betterDeals: 'better deals',
      viewDetails: 'View details',
      settings: 'Auto-renewal settings',
    },
    comparison: {
      title: 'Compare options',
      current: 'Current policy',
      recommended: 'Recommended',
      coverage: 'Coverage',
      price: 'Price',
      score: 'Score',
      savings: 'Savings',
      switch: 'Switch to this',
      renew: 'Renew with current',
      noRecommendations: 'No recommendations yet. Run a check to find better deals.',
      share: 'Share',
    },
    gap: {
      warning: 'Coverage gap',
      message: 'Switching may leave you without coverage for {{days}} days.',
    },
    settings: {
      title: 'Auto-renewal settings',
      enabled: 'Auto-renewal',
      preferCurrent: 'Prefer current insurer',
      maxIncrease: 'Max price increase %',
      notifyBefore: 'Notify me (days before)',
      save: 'Save',
      back: 'Back to renewal',
    },
    calendar: {
      title: 'Renewal calendar',
      noEvents: 'No renewals in this range.',
      noRenewals: 'No renewals',
      noRenewalsDesc: 'No policies expiring in the next 90 days.',
      listView: 'List view',
      policiesOnDate: 'Policies expiring this day',
      legendCritical: '<30 days',
      legendUrgent: '31-60 days',
      legendMedium: '61-90 days',
      legendGrey: '>90 days',
    },
    history: {
      title: 'Recommendation history',
      noHistory: 'No past recommendations.',
      noPremiumHistory: 'No premium history.',
      notEnoughData: 'Not Enough Data Yet',
      notEnoughDataDesc: 'Premium history will appear after your first renewal',
      date: 'Date',
      amount: 'Amount',
      change: 'Change',
      reason: 'Reason',
      avgIncrease: 'Avg. annual increase',
    },
    changeReasons: {
      renewal: 'Annual renewal',
      rate_change: 'Rate change',
      coverage_change: 'Coverage change',
      other: 'Other',
    },
    share: {
      title: 'Share recommendation',
      email: 'Email address',
      send: 'Send',
      cancel: 'Cancel',
      sent: 'Recommendation shared successfully.',
      failed: 'Failed to share. Try again.',
      linkCopied: 'Link copied to clipboard.',
      whatsappMessage: 'Better deal: {{policyName}}. Save {{savings}}/mo with {{insurer}}.',
    },
    nav: { renewals: 'Renewals' },
    errors: {
      expired: 'Expired',
      checkInProgress: 'Checking...',
      noRenewals: 'No Renewals Upcoming',
      noRenewalsDesc: "Your policies aren't due for renewal yet. Add policies to your wallet to track renewals.",
      addPolicy: 'Add Policy',
    },
    checkResult: {
      betterDeals: '{{count}} better deals found. Save up to ${{amount}}/mo.',
      noBetterDeals: 'Check completed. Your policy is competitive.',
    },
  },
};

const ar = {
  renewals: {
    dashboard: {
      title: 'ذكاء التجديد',
      stats: { expiring: 'تنتهي قريباً', savings: 'مدخرات محتملة', recommendations: 'عروض أفضل', checks: 'فحوصات' },
      lastUpdated: 'آخر تحديث',
      newDealsFound: 'تم العثور على {{count}} عرض أفضل جديد!',
      upcoming: 'التجديدات القادمة',
      viewCalendar: 'عرض التقويم',
      comparePolicies: 'مقارنة الوثائق',
      noPolicies: 'لا توجد وثائق تنتهي قريباً.',
      runCheck: 'ابحث عن عروض أفضل',
      checkNow: 'فحص الآن',
    },
    card: {
      daysLeft: 'أيام متبقية',
      expires: 'تنتهي',
      premium: 'القسط',
      betterDeals: 'عروض أفضل',
      viewDetails: 'التفاصيل',
      settings: 'إعدادات التجديد التلقائي',
    },
    comparison: {
      title: 'مقارنة الخيارات',
      current: 'الوثيقة الحالية',
      recommended: 'موصى به',
      coverage: 'التغطية',
      price: 'السعر',
      score: 'النقاط',
      savings: 'الادخار',
      switch: 'التحويل إلى هذا',
      renew: 'التجديد مع الحالي',
      noRecommendations: 'لا توصيات بعد. قم بالفحص للعثور على عروض أفضل.',
      share: 'مشاركة',
    },
    gap: {
      warning: 'فجوة تغطية',
      message: 'التحويل قد يتركك بدون تغطية لـ {{days}} أيام.',
    },
    settings: {
      title: 'إعدادات التجديد التلقائي',
      enabled: 'التجديد التلقائي',
      preferCurrent: 'تفضيل شركة التأمين الحالية',
      maxIncrease: 'أقصى زيادة في السعر %',
      notifyBefore: 'تنبيه قبل (أيام)',
      save: 'حفظ',
      back: 'العودة للتجديد',
    },
    calendar: {
      title: 'تقويم التجديدات',
      noEvents: 'لا تجديدات في هذه الفترة.',
      noRenewals: 'لا تجديدات',
      noRenewalsDesc: 'لا وثائق تنتهي خلال ٩٠ يوماً.',
      listView: 'عرض القائمة',
      policiesOnDate: 'وثائق تنتهي في هذا اليوم',
      legendCritical: 'أقل من ٣٠ يوماً',
      legendUrgent: '٣١-٦٠',
      legendMedium: '٦١-٩٠',
      legendGrey: 'أكثر من ٩٠',
    },
    history: {
      title: 'سجل التوصيات',
      noHistory: 'لا توصيات سابقة.',
      noPremiumHistory: 'لا سجل أقساط.',
      notEnoughData: 'لا توجد بيانات كافية بعد',
      notEnoughDataDesc: 'سيظهر سجل الأقساط بعد أول تجديد',
      date: 'التاريخ',
      amount: 'المبلغ',
      change: 'التغيير',
      reason: 'السبب',
      avgIncrease: 'متوسط الزيادة السنوية',
    },
    changeReasons: {
      renewal: 'تجديد سنوي',
      rate_change: 'تغيير السعر',
      coverage_change: 'تغيير التغطية',
      other: 'أخرى',
    },
    share: {
      title: 'مشاركة التوصية',
      email: 'البريد الإلكتروني',
      send: 'إرسال',
      cancel: 'إلغاء',
      sent: 'تم مشاركة التوصية بنجاح.',
      failed: 'فشل الإرسال. حاول مرة أخرى.',
      linkCopied: 'تم نسخ الرابط.',
      whatsappMessage: 'صفقة أفضل: {{policyName}}. وفّر {{savings}}/شهر مع {{insurer}}.',
    },
    nav: { renewals: 'التجديدات' },
    errors: {
      expired: 'منتهية',
      checkInProgress: 'جاري الفحص...',
      noRenewals: 'لا تجديدات قادمة',
      noRenewalsDesc: 'وثائقك ليست قريبة من التجديد بعد. أضف وثائق إلى محفظتك لمتابعة التجديدات.',
      addPolicy: 'إضافة وثيقة',
    },
    checkResult: {
      betterDeals: 'تم العثور على {{count}} عروض أفضل. وفّر حتى ${{amount}}/شهر.',
      noBetterDeals: 'تم الفحص. بوليصتك تنافسية.',
    },
  },
};

const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';
const t = (key, params) => {
  const obj = locale === 'ar' ? ar : en;
  const parts = key.split('.');
  let v = obj;
  for (const p of parts) v = v?.[p];
  let str = typeof v === 'string' ? v : key;
  if (params && typeof str === 'string') {
    str = Object.keys(params).reduce((s, k) => s.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), params[k]), str);
  }
  return str;
};

export { en, ar, t };
export default t;
